// ============================================================
// caseNavFold.ts — ONE fold rule for the case menu on EVERY case section
// (tested in caseNavFold.test.tsx).
//
//   • AUTO (until the user chooses): folded to an icon rail when the case
//     CONTENT area is narrower than CASE_NAV_AUTO_FOLD_BELOW px (measured with
//     a ResizeObserver on the content area — not the window, so the main
//     sidebar's own collapse counts); unfolded when it's wider.
//   • The user's CHOICE (the rail's chevron / "Fold menu") overrides auto from
//     then on, on every case section, until they change it again. It's
//     remembered per user with the same mechanism as the main sidebar's
//     collapse (usePersistedToggle → User.viewingPaneToggles, PATCHed):
// (Keys are lower-case before the dot: the server accepts toggle keys matching
// /^[a-z]+\.[a-zA-Z]+$/ only — backend authController — so "caseNav.*" would
// 400 and take the whole toggles save (incl. sidebar.collapsed) down with it.)
//       casenav.foldChosen = true once they've chosen
//       casenav.folded     = their choice
//     There's no separate "automatic" switch: a user who has chosen keeps
//     their choice (clearing casenav.foldChosen restores auto).
// ============================================================
import { useCallback, useEffect, useState } from 'react'
import { usePersistedToggle } from '../../hooks/usePersistedToggle'

// 1200px of CONTENT width: a 1440 screen with the main sidebar open leaves
// ~1232px, so the case menu stays open there; it folds on 1280 screens.
export const CASE_NAV_AUTO_FOLD_BELOW = 1200

export const resolveCaseNavFold = (o: { chosen: boolean; folded: boolean; contentWidth: number | null }): boolean =>
  o.chosen ? o.folded : (o.contentWidth !== null && o.contentWidth < CASE_NAV_AUTO_FOLD_BELOW)

// An element's width, tracked live. Returns a CALLBACK ref (attach it with
// ref={measureRef}) so measuring starts whenever the element appears — e.g.
// after a loading skeleton — not just on first mount. null until measured
// (or without ResizeObserver).
export const useElementWidth = (): [(el: HTMLElement | null) => void, number | null] => {
  const [el, setEl] = useState<HTMLElement | null>(null)
  const [w, setW] = useState<number | null>(null)
  const measureRef = useCallback((node: HTMLElement | null) => setEl(node), [])
  useEffect(() => {
    if (!el) return
    setW(el.getBoundingClientRect().width)
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])
  return [measureRef, w]
}

// → [folded, toggle, measureRef]. Attach measureRef to the case page's
// content area.
export const useCaseNavFold = (): [boolean, () => void, (el: HTMLElement | null) => void] => {
  const [chosen, setChosen] = usePersistedToggle('casenav.foldChosen', false)
  const [folded, setFolded] = usePersistedToggle('casenav.folded', false)
  const [measureRef, width] = useElementWidth()
  const effective = resolveCaseNavFold({ chosen, folded, contentWidth: width })
  const toggle = (): void => {
    setFolded(!effective)
    if (!chosen) setChosen(true)
  }
  return [effective, toggle, measureRef]
}
